import React, { lazy, Suspense, useContext, useEffect, useState } from "react";
import { Routes, Route, useNavigate, useSearchParams } from "react-router-dom";
import * as URL from "./Routes";
import ProtectedRoute from "src/helpers/ProtectedRoute";
import WardenManageHostel from "src/views/pages/Hotel/WardenManageHostel";
import AuthContext from "src/helpers/AuthContext";
import { useMutation } from "react-query";
import { LoginWithToken } from "src/api/services/Login";
import { toast } from "react-toastify";


const Login = lazy(() => import("../views/pages/Login/Login"));
const Register = lazy(() => import("../views/pages/register/Register"));
const Page404 = lazy(() => import("../views/pages/page404/Page404"));
const Page505 = lazy(() => import("../views/pages/page500/Page500"));
const Dashboard = lazy(() => import("../views/dashboard/Dashboard"));
const RoomListTk = lazy(() => import("../views/pages/RoomListTk/List"));
const Profile = lazy(() => import("../views/pages/profile/UserProfile"));


const UserTypeList = lazy(() => import("../views/pages/UserType/List"));
const AddUserType = lazy(() => import("../views/pages/UserType/AddUserType/AddUserType"));
const EditUserType = lazy(() => import("../views/pages/UserType/EditUserType/EditUserType"));


const RoomTypeList = lazy(() => import("../views/pages/RoomType/List"));
const AddRoomType = lazy(() => import("../views/pages/RoomType/AddRoomType/AddRoomType"));
const EditRoomType = lazy(() => import("../views/pages/RoomType/EditRoomType/EditRoomType"));


const UserList = lazy(() => import("../views/pages/Users/List"));
const AddUser = lazy(() => import("../views/pages/Users/Add/AddUser"));
const EditUser = lazy(() => import("../views/pages/Users/Edit/EditUser"));
const PermissionView = lazy(() => import("../views/pages/Users/View/View"));


const HotelList = lazy(() => import("../views/pages/Hotel/List"));
const AddHotel = lazy(() => import("../views/pages/Hotel/Add/AddHotel"));
const EditHotel = lazy(() => import("../views/pages/Hotel/Edit/EditHotel"));
const HotelView = lazy(() => import("../views/pages/Hotel/View/View"));
const ViewHotelRoom = lazy(() => import("../views/pages/Hotel/EditRoom/EditRoom"));


const VisitorList = lazy(() => import("../views/pages/Visitors/VisitorsList"));
const AddVisitors = lazy(() => import("../views/pages/Visitors/AddVisitors/AddVisitors"));
const EditVisitor = lazy(() => import("../views/pages/Visitors/Edit/EditVisitor"));
const VisiterView = lazy(() => import("../views/pages/Visitors/View/View"));


const ReservationList = lazy(() => import("../views/pages/Reservation/List"));
const BulkReservation = lazy(() => import("../views/pages/Reservation/Bulk/Bulk"));
const ReservationView = lazy(() => import("../views/pages/Reservation/View/View"));
const EditReservation = lazy(() => import("../views/pages/Reservation/Edit/EditReservation"));
const CheckInCheckOutList = lazy(() => import("../views/pages/Reservation/CheckInCheckOut/List"));
const AddReservation = lazy(() => import("../views/pages/Reservation/Add/AddReservation"));
const AddHotelReservation = lazy(() => import("../views/pages/Reservation/AddHotelReservation/AddHotelReservation"));


const RoomMaintenance = lazy(() => import("../views/pages/RoomMaintenance/RoomMaintenance"));

const Layout = lazy(() => import("../layout/Layout"));


const Loader = () => (
  <div className="pt-3 text-center min-vh-100 d-flex flex-column justify-content-center align-items-center">
    <div className="sk-spinner sk-spinner-pulse"></div>
    <div className="mt-3 text-muted">Authenticating...</div>
  </div>
);

export default function Router() {

  const { login } = useContext(AuthContext)

  const Navigate = useNavigate();

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  const token = getCookie("authToken") || getCookie("access_token_admin");


  const [isVerifyingToken, setIsVerifyingToken] = useState(!!token);

  const LoginWithTokenAPI = useMutation({
    mutationKey: ['LoginWithToken'],
    mutationFn: () => LoginWithToken(),
    onSuccess: (obj) => handleSuccess(obj),
    onError: (err) => handleError(err),
    retry: false,
    refetchOnWindowsFocus: false,
  })

  const handleSuccess = async (obj) => {
    await login(obj)
    setIsVerifyingToken(false);
    Navigate('/dashboard', { replace: true })
  }

  const handleError = (errObj) => {
    const errMessage = errObj.response?.data?.message || 'Login failed'
    toast.error(errMessage, {
      position: toast.POSITION.TOP_RIGHT,
    })
    setIsVerifyingToken(false);
    Navigate('/login', { replace: true });
  }

  useEffect(() => {

    if (token) {
      LoginWithTokenAPI.mutate();
      return;
    }

    if (window.location.pathname === "/" && !token) Navigate("/login", { replace: true });
  }, [token]);


  if (isVerifyingToken || LoginWithTokenAPI.isLoading) {
    return <Loader />;
  }

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route
          path={URL.Login}
          element={
            <ProtectedRoute accessBy="non-authenticated">
              <Login />
            </ProtectedRoute>
          }
        />

        {/* All authenticated routes share a single Layout (sidebar + header persist) */}
        <Route
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path={URL.Dashboard} element={<Dashboard />} />
          <Route path="room/list" element={<RoomListTk />} />
          <Route path={URL.UserProfile} element={<Profile />} />

          {/* --- User Type Routes --- */}
          <Route path={URL.UserTypeList} element={<UserTypeList />} />
          <Route path={URL.AddUserType} element={<AddUserType />} />
          <Route path={URL.EditUserTypeId} element={<EditUserType />} />

          {/* --- Room Type Routes --- */}
          <Route path={URL.RoomTypeList} element={<RoomTypeList />} />
          <Route path={URL.AddRoomType} element={<AddRoomType />} />
          <Route path={URL.EditRoomTypeId} element={<EditRoomType />} />

          {/* --- User Routes --- */}
          <Route path={URL.UserList} element={<UserList />} />
          <Route path={URL.AddUser} element={<AddUser />} />
          <Route path={URL.EditUserId} element={<EditUser />} />
          <Route path={URL.ViewPermission} element={<PermissionView />} />

          {/* --- Hotel / Hostel Routes --- */}
          <Route path={URL.HotelList} element={<HotelList />} />
          <Route path={URL.AddHotel} element={<AddHotel />} />
          <Route path={URL.EditHotelId} element={<EditHotel />} />
          <Route path={URL.ViewHotelId} element={<HotelView />} />
          <Route path={URL.ViewHotelRoomId} element={<ViewHotelRoom />} />

          {/* WARDEN MANAGE HOSTEL ROUTE */}
          <Route path={URL.manageHostel} element={<WardenManageHostel />} />

          {/* --- Visitor Routes --- */}
          <Route path={URL.VisitorList} element={<VisitorList />} />
          <Route path={URL.EditVisitorId} element={<EditVisitor />} />
          <Route path={URL.AddVisitors} element={<AddVisitors />} />
          <Route path={URL.EditVisitor} element={<EditVisitor />} />
          <Route path={URL.ViewVisiterId} element={<VisiterView />} />

          {/* --- Reservation Routes --- */}
          <Route path={URL.ReservationList} element={<ReservationList />} />
          <Route path={URL.BulkReservation} element={<BulkReservation />} />
          <Route path={URL.ViewReservationId} element={<ReservationView />} />
          <Route path={URL.EditReservationId} element={<EditReservation />} />
          <Route path={URL.CheckInCheckOutList} element={<CheckInCheckOutList />} />
          <Route path={URL.AddReservation} element={<AddReservation />} />
          <Route path={URL.AddHotelReservationId} element={<AddHotelReservation />} />

          {/* Room Maintenance Route */}
          <Route path={URL.RoomMaintenance} element={<RoomMaintenance />} />
        </Route>

        <Route path={URL.Register} element={<Register />} />
        <Route path={URL.Page500} element={<Page505 />} />
        <Route path="*" element={<Page404 />} />
      </Routes>
    </Suspense>
  );
}